import React, { useState } from 'react';
import Card from '../../../../shared/components/Card';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

interface IncomeRow {
  id: string;
  name: string;
  amount: number;
}

interface ExpenseRow {
  id: string;
  amount: number;
  name: string;
  description: string;
  date: string;
}

const MOCK_INCOME: IncomeRow[] = [
  { id: '1', name: 'Mickadu21', amount: 17613 },
  { id: '2', name: 'Matteospv', amount: 12293 },
  { id: '3', name: 'Fr3d', amount: 10675 },
];

const MOCK_EXPENSES: ExpenseRow[] = [
  { id: '1', amount: 200000, name: 'M0rgan', description: 'CH Alliance 54 Piedmont 1 - Garde prolongée', date: '15 Févr. 17:12' },
  { id: '2', amount: 70000, name: 'Wiglaum', description: 'Formation secours nautique', date: '14 Févr. 09:30' },
  { id: '3', amount: 10000, name: 'Victor77', description: 'Matériel commun', date: '13 Févr. 14:00' },
];

const BALANCE = 6287292779;
const EXPENSE_PAGE_SIZE = 10;

const AllianceBank: React.FC = () => {
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [byDay, setByDay] = useState(true);
  const [expensePage, setExpensePage] = useState(1);
  /** Taux de cotisation choisi par le joueur : 0 % à 10 %, par défaut 0 %. */
  const [contributionPercent, setContributionPercent] = useState(0);

  const formatAmount = (n: number) =>
    new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

  const totalExpensePages = Math.max(1, Math.ceil(MOCK_EXPENSES.length / EXPENSE_PAGE_SIZE));
  const paginatedExpenses = MOCK_EXPENSES.slice(
    (expensePage - 1) * EXPENSE_PAGE_SIZE,
    expensePage * EXPENSE_PAGE_SIZE
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Journal des revenus */}
      <Card className="p-6 lg:col-span-1">
        <h2 className="text-xl font-bold text-white mb-4">{t('bank.incomeJournal')}</h2>
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setByDay(true)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${byDay ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
          >
            {t('bank.byDay')}
          </button>
          <button
            type="button"
            onClick={() => setByDay(false)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${!byDay ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
          >
            {t('bank.byMonth')}
          </button>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-800/80 border-b border-gray-700">
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('bank.colName')}</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('bank.colAmount')}</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_INCOME.map((row) => (
                <tr key={row.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3 text-gray-300">{row.name}</td>
                  <td className="px-4 py-3 text-gray-400 tabular-nums">{formatAmount(row.amount)} {t('bank.currency')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Banque : solde, contributions, dépenses */}
      <Card className="p-6 lg:col-span-2 flex flex-col">
        <h2 className="text-2xl font-bold text-white mb-4">{t('bank.title')}</h2>
        <p className="text-3xl font-bold text-indigo-400 mb-2">
          {formatAmount(BALANCE)} {t('bank.currency')}
        </p>
        <p className="text-sm text-gray-500 mb-6">{t('bank.balance')}</p>

        <section className="mb-6">
          <h3 className="text-sm font-semibold text-gray-400 mb-2">{t('bank.yourContributions')}</h3>
          <p className="text-xs text-gray-500 mb-3">{t('bank.contributionsHelp')}</p>
          <div className="flex flex-wrap gap-2">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pct) => (
              <button
                key={pct}
                type="button"
                onClick={() => setContributionPercent(pct)}
                className={`w-10 h-10 rounded-lg text-sm font-medium tabular-nums transition-colors ${
                  contributionPercent === pct
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-gray-300'
                }`}
              >
                {pct} %
              </button>
            ))}
          </div>
        </section>

        <section className="flex-1 min-h-0 flex flex-col">
          <h3 className="text-lg font-semibold text-gray-300 mb-3">{t('bank.expenses')}</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-700 flex-1">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-800/80 border-b border-gray-700">
                  <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('bank.colAmount')}</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('bank.colName')}</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('bank.colDescription')}</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('bank.colDate')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedExpenses.map((row) => (
                  <tr key={row.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                    <td className="px-4 py-3 text-gray-400 tabular-nums">{formatAmount(row.amount)} {t('bank.currency')}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium text-indigo-400 bg-indigo-500/20 border border-indigo-500/40 hover:bg-indigo-500/30 hover:border-indigo-400/60 hover:text-indigo-300 transition-colors text-left"
                      >
                        {row.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm max-w-xs truncate" title={row.description}>{row.description}</td>
                    <td className="px-4 py-3 text-gray-400 text-sm">{row.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-sm text-gray-400">
            <button
              type="button"
              onClick={() => setExpensePage((p) => Math.max(1, p - 1))}
              disabled={expensePage <= 1}
              className="hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('bank.paginationPrev')}
            </button>
            <span>{expensePage} … {totalExpensePages}</span>
            <button
              type="button"
              onClick={() => setExpensePage((p) => Math.min(totalExpensePages, p + 1))}
              disabled={expensePage >= totalExpensePages}
              className="hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('bank.paginationNext')}
            </button>
          </div>
        </section>
      </Card>
    </div>
  );
};

export default AllianceBank;
